import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Phone, PhoneOff, Video } from "lucide-react";
import { toast } from "sonner";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";
import { useAlertStream, sendAlert } from "@/hooks/platform/useAlerts";
import { usePresenceBroadcast } from "@/hooks/platform/usePresence";
import CallDialog from "@/components/platform/CallDialog";
import type { NotificationRow } from "@/hooks/platform/useNotifications";

interface Incoming {
  callerId: string | null;
  callerName: string;
  callerAvatar: string | null;
  mode: "voice" | "video";
}

/** Platform-wide push listener: rings for incoming calls and toasts live-room invites. */
const IncomingCallListener = () => {
  const { userId } = useCurrentUser();
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState<Incoming | null>(null);
  const [inCall, setInCall] = useState<Incoming | null>(null);

  usePresenceBroadcast();

  const handle = useCallback((row: NotificationRow) => {
    const meta = (row.metadata ?? {}) as Record<string, unknown>;

    if (row.type === "call_incoming") {
      setIncoming({
        callerId: (meta.from as string) ?? null,
        callerName: (meta.callerName as string) ?? row.title,
        callerAvatar: (meta.callerAvatar as string) ?? null,
        mode: (meta.mode as "voice" | "video") ?? "voice",
      });
      return;
    }

    if (row.type === "live_invite" || row.type === "live_guest") {
      toast(row.title, {
        description: row.body ?? undefined,
        action: row.link ? { label: "Join", onClick: () => navigate(row.link!) } : undefined,
        duration: 12000,
      });
      return;
    }

    if (row.type === "gift" || row.type === "call_missed") {
      toast(row.title, { description: row.body ?? undefined });
    }
  }, [navigate]);

  useAlertStream(userId, handle);

  const decline = async () => {
    const caller = incoming;
    setIncoming(null);
    if (caller?.callerId) {
      try {
        await sendAlert({
          userId: caller.callerId,
          type: "call_missed",
          title: "Call declined",
          body: "They couldn't take your call right now — try again or send a message.",
          link: "/messages",
        });
      } catch { /* best effort */ }
    }
  };

  const accept = () => { setInCall(incoming); setIncoming(null); };

  return (
    <>
      <Dialog open={!!incoming} onOpenChange={(o) => { if (!o) void decline(); }}>
        <DialogContent className="max-w-xs border-border bg-background text-center">
          <div className="flex flex-col items-center gap-3 py-2">
            <Badge className="border-0 bg-primary/20 text-foreground">
              Incoming {incoming?.mode === "video" ? "video" : "voice"} call
            </Badge>
            <Avatar className="h-20 w-20 border-4 border-primary/30">
              <AvatarImage src={incoming?.callerAvatar ?? undefined} />
              <AvatarFallback className="text-xl">{(incoming?.callerName ?? "MC").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <p className="text-lg font-semibold text-foreground">{incoming?.callerName}</p>
            <p className="text-xs text-muted-foreground">is calling you now</p>
            <div className="mt-2 flex items-center gap-8">
              <button type="button" aria-label="Decline call" onClick={() => void decline()} className="flex flex-col items-center gap-1">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
                  <PhoneOff className="h-5 w-5" />
                </span>
                <span className="text-xs text-muted-foreground">Decline</span>
              </button>
              <button type="button" aria-label="Accept call" onClick={accept} className="flex flex-col items-center gap-1">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-600 text-white">
                  {incoming?.mode === "video" ? <Video className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
                </span>
                <span className="text-xs text-muted-foreground">Accept</span>
              </button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => { setIncoming(null); navigate("/messages"); }}>
              Reply with a message
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CallDialog
        open={!!inCall}
        onOpenChange={(o) => !o && setInCall(null)}
        peer={{ id: inCall?.callerId, name: inCall?.callerName ?? "Member", avatar: inCall?.callerAvatar }}
        mode={inCall?.mode ?? "voice"}
      />
    </>
  );
};

export default IncomingCallListener;
